/* global debounce */

if (!customElements.get('predictive-search')) {
  const SEARCH_VOCABULARY = [
    'kakel',
    'klinker',
    'mosaik',
    'badrum',
    'tvättställ',
    'blandare',
    'kommod',
    'dusch',
    'duschvägg',
    'duschdörr',
    'duschhörna',
    'takdusch',
    'handdukstork',
    'toalett',
    'toalettstol',
    'wc',
    'spegel',
    'spegelskåp',
    'badrumsspegel',
    'golvbrunn',
    'fixtur',
    'wc-fixtur',
    'kakelfix',
    'fix',
    'fog',
    'silikon',
    'tätskikt',
    'primer',
    'granitkeramik',
    'natursten',
    'marmor',
    'terrazzo',
    'cement',
    'dekor',
    'list',
    'sockel',
    'golv',
    'vägg',
    'väggkakel',
    'golvklinker',
    'utomhus',
    'uteklinker',
    'pool',
    'terrass',

    'blank',
    'matt',
    'polerad',
    'strukturerad',
    'rektifierad',
    'vit',
    'svart',
    'beige',
    'grå',
    'brun',
    'grön',
    'blå',
    'rosa',
    'mässing',
    'krom',
    'borstad',
    'rostfri',

    // Danska/norska termer som vi inte vill råka korrigera till svenska.
    'flise',
    'fliser',
    'servant',
    'armatur',
    'blandebatteri',
    'dusj',
    'dusjvegg',
    'speil'
  ];


  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.cache = {};
      this.requestId = 0;
      this.correctedQuery = null;

      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');

      this.productTypeInput = this.querySelector(
        'input[name="filter.p.product_type"]'
      );

      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');

      this.loadingText = this.getAttribute('data-loading-text');

      this.correctionLabel =
        this.getAttribute('data-correction-label') ||
        'Visar resultat för';

      this.addListeners();
    }

    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener(
        'focus',
        this.handleFocus.bind(this)
      );

      this.input.addEventListener(
        'input',
        debounce(this.handleInput.bind(this))
      );

      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener(
          'change',
          this.handleProductTypeChange.bind(this)
        );
      }

      this.querySelector('.search').addEventListener(
        'submit',
        this.handleSubmit.bind(this)
      );
    }

    getQuery() {
      return this.input.value.trim();
    }

    handleSubmit(evt) {
      if (!this.getQuery().length) {
        evt.preventDefault();
        return;
      }

      const selectedNormalResult = this.querySelector(
        '.predictive-search__item[aria-selected="true"]:not(.js-submit)'
      );

      if (selectedNormalResult) {
        evt.preventDefault();
        return;
      }

      /*
       * Om fuzzy fallback har hittat t.ex.
       *
       * kakle -> kakel
       *
       * ska även "Visa alla" / Enter söka på "kakel".
       */
      if (this.correctedQuery) {
        this.input.value = this.correctedQuery;
        this.input.setAttribute(
          'value',
          this.correctedQuery
        );
      }
    }

    handleInput() {
      this.input.setAttribute(
        'value',
        this.input.value
      );

      this.correctedQuery = null;

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    handleProductTypeChange(evt) {
      if (this.productTypeInput) {
        this.productTypeInput.value =
          evt.detail.selectedValue;
      }

      this.correctedQuery = null;

      const query = this.getQuery();

      if (query.length) {
        this.getResults(query);
      }
    }

    handleFocus() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        return;
      }

      if (
        this.getAttribute('results') === 'true'
      ) {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    handleKeydown(evt) {
      if (
        evt.target.matches('.tablist__tab')
      ) {
        return;
      }

      if (
        evt.key === 'ArrowUp' ||
        evt.key === 'ArrowDown'
      ) {
        evt.preventDefault();

        if (this.hasAttribute('open')) {
          this.handleResultsNav(evt.key);
        }

        return;
      }

      if (evt.key === 'Enter') {
        this.selectOption();
        return;
      }

      if (evt.key === 'Escape') {
        this.close();
      }
    }

    handleKeyup() {
      if (!this.getQuery().length) {
        this.close();
      }
    }

    handleResultsNav(key) {
      const selectedResult =
        this.querySelector(
          '[role="tabpanel"] [aria-selected="true"]'
        );

      const allResults = Array.from(
        this.querySelectorAll(
          '[role="tabpanel"] .predictive-search__item'
        )
      ).filter(
        (item) => item.offsetParent !== null
      );

      if (!allResults.length) {
        return;
      }

      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) {
          return;
        }

        const index =
          allResults.indexOf(selectedResult);

        resultToSelect =
          allResults[
            index > 0
              ? index - 1
              : allResults.length - 1
          ];
      } else if (selectedResult) {
        const index =
          allResults.indexOf(selectedResult);

        resultToSelect =
          allResults[
            index < allResults.length - 1
              ? index + 1
              : 0
          ];
      }

      if (
        selectedResult &&
        selectedResult !== resultToSelect
      ) {
        selectedResult.setAttribute(
          'aria-selected',
          'false'
        );
      }

      resultToSelect.setAttribute(
        'aria-selected',
        'true'
      );

      this.setLiveRegionText(
        resultToSelect.textContent
      );

      if (resultToSelect.id) {
        this.input.setAttribute(
          'aria-activedescendant',
          resultToSelect.id
        );
      }
    }

    selectOption() {
      const selected =
        this.querySelector(
          '.predictive-search__item[aria-selected="true"] .js-search-link'
        );

      if (selected) {
        selected.click();
      }
    }

    buildSearchParams(searchTerm) {
      let searchFields =
        'title,product_type,variants.title,vendor';

      if (
        theme.settings.pSearchIncludeSkus
      ) {
        searchFields += ',variants.sku';
      }

      if (
        theme.settings.pSearchIncludeTags
      ) {
        searchFields += ',tag';
      }

      let q =
        encodeURIComponent(searchTerm);

      if (
        this.productTypeInput &&
        this.productTypeInput.value
      ) {
        q =
          `product_type:${encodeURIComponent(
            this.productTypeInput.value
          )} AND ${q}`;
      }

      const params = [
        `q=${q}`,

        `${encodeURIComponent(
          'resources[type]'
        )}=product,collection`,

        `${encodeURIComponent(
          'resources[limit]'
        )}=${theme.settings.pSearchLimit}`,

        `${encodeURIComponent(
          'resources[limit_scope]'
        )}=${theme.settings.pSearchLimitScope}`,

        `${encodeURIComponent(
          'resources[options][fields]'
        )}=${searchFields}`,

        `${encodeURIComponent(
          'resources[options][prefix]'
        )}=last`,

        'section_id=predictive-search'
      ];

      return params.join('&');
    }

    async fetchResultsMarkup(searchTerm) {
      const searchParams =
        this.buildSearchParams(searchTerm);

      const key =
        searchParams.toLowerCase();

      if (this.cache[key]) {
        return this.cache[key];
      }

      const response = await fetch(
        `${theme.routes.predictiveSearch}?${searchParams}`
      );

      if (!response.ok) {
        throw new Error(
          `Predictive search failed: ${response.status}`
        );
      }

      const template =
        document.createElement('template');

      template.innerHTML =
        await response.text();

      const section =
        template.content.querySelector(
          '#shopify-section-predictive-search'
        );

      if (!section) {
        throw new Error(
          'Predictive search section saknas i svaret.'
        );
      }

      const markup =
        section.innerHTML.replace(
          /psearch/g,
          this.input.id
        );

      this.cache[key] = markup;

      return markup;
    }

    /*
     * "Visa alla"-knappen räknas inte som
     * ett faktiskt sökresultat.
     */
   hasUsableResults(markup) {
  const template =
    document.createElement('template');

  template.innerHTML = markup;

  return Boolean(
    template.content.querySelector(
      '.predictive-search__item:not(.js-submit):not(.predictive-search__suggestion)'
    )
  );
}

    normalize(value) {
      return value
        .toLocaleLowerCase('sv-SE')
        .trim()
        .replace(/[’']/g, '')
        .replace(
          /[^\p{L}\p{N}-]/gu,
          ''
        );
    }

    /*
     * Damerau-Levenshtein.
     *
     * Hanterar bland annat:
     *
     * kakle -> kakel
     *
     * eftersom två bokstäver som hamnat
     * i fel ordning räknas som ett fel.
     */
    editDistance(source, target) {
      const a = Array.from(source);
      const b = Array.from(target);

      const matrix = Array.from(
        {
          length: a.length + 1
        },
        () =>
          Array(
            b.length + 1
          ).fill(0)
      );

      for (
        let i = 0;
        i <= a.length;
        i += 1
      ) {
        matrix[i][0] = i;
      }

      for (
        let j = 0;
        j <= b.length;
        j += 1
      ) {
        matrix[0][j] = j;
      }

      for (
        let i = 1;
        i <= a.length;
        i += 1
      ) {
        for (
          let j = 1;
          j <= b.length;
          j += 1
        ) {
          const cost =
            a[i - 1] === b[j - 1]
              ? 0
              : 1;

          matrix[i][j] =
            Math.min(
              matrix[i - 1][j] + 1,
              matrix[i][j - 1] + 1,
              matrix[i - 1][j - 1] +
                cost
            );

          if (
            i > 1 &&
            j > 1 &&
            a[i - 1] === b[j - 2] &&
            a[i - 2] === b[j - 1]
          ) {
            matrix[i][j] =
              Math.min(
                matrix[i][j],
                matrix[i - 2][j - 2] +
                  1
              );
          }
        }
      }

      return matrix[a.length][b.length];
    }

    correctToken(token) {
      const normalized =
        this.normalize(token);

      if (!normalized) {
        return token;
      }

      /*
       * SKU, storlekar, modellnummer osv.
       * ska inte fuzzy-korrigeras.
       *
       * Ex:
       * 60x60
       * 120x120
       * ABC123
       */
      if (/\d/.test(normalized)) {
        return token;
      }

      /*
       * Korta ord ska inte korrigeras
       * aggressivt.
       *
       * Ex:
       * vit
       * grå
       * wc
       */
      if (normalized.length < 4) {
        return token;
      }

      let bestMatch = null;
      let bestDistance = Infinity;
      let tied = false;

      SEARCH_VOCABULARY.forEach(
        (candidate) => {
          const normalizedCandidate =
            this.normalize(candidate);

          if (
            Math.abs(
              normalizedCandidate.length -
                normalized.length
            ) > 2
          ) {
            return;
          }

          const distance =
            this.editDistance(
              normalized,
              normalizedCandidate
            );

          if (
            distance < bestDistance
          ) {
            bestDistance = distance;
            bestMatch = candidate;
            tied = false;
          } else if (
            distance === bestDistance
          ) {
            tied = true;
          }
        }
      );

      /*
       * Ord på 4-7 tecken:
       * max 1 fel.
       *
       * Längre ord:
       * max 2 fel.
       */
      const maxDistance =
        normalized.length >= 8
          ? 2
          : 1;

      /*
       * Om två ord är exakt lika bra
       * gissar vi inte.
       */
      if (
        !tied &&
        bestMatch &&
        bestDistance <= maxDistance
      ) {
        return bestMatch;
      }

      return token;
    }

    /*
     * Korrigera varje ord separat.
     *
     * Ex:
     *
     * "svart blndare"
     *
     * blir:
     *
     * "svart blandare"
     */
    getCorrectionCandidate(searchTerm) {
      const parts =
        searchTerm.split(/(\s+)/);

      let changed = false;

      const corrected = parts
        .map((part) => {
          if (!part.trim()) {
            return part;
          }

          const leading =
            part.match(
              /^[^\p{L}\p{N}]+/u
            )?.[0] || '';

          const trailing =
            part.match(
              /[^\p{L}\p{N}]+$/u
            )?.[0] || '';

          const end =
            trailing.length
              ? part.length -
                trailing.length
              : part.length;

          const core = part.slice(
            leading.length,
            end
          );

          if (!core) {
            return part;
          }

          const correctedCore =
            this.correctToken(core);

          if (
            this.normalize(
              correctedCore
            ) !==
            this.normalize(core)
          ) {
            changed = true;
          }

          return `${leading}${correctedCore}${trailing}`;
        })
        .join('');

      return changed
        ? corrected.trim()
        : null;
    }

    async getResults(searchTerm) {
      /*
       * Varje ny tangenttryckning får
       * ett nytt ID.
       *
       * Det förhindrar ett långsamt gammalt
       * request från att skriva över ett
       * nyare sökresultat.
       */
      const requestId =
        ++this.requestId;

      this.setLiveRegionLoadingState();

      try {
        /*
         * STEG 1:
         *
         * Kör vanlig Shopify-sökning först.
         */
        const originalMarkup =
          await this.fetchResultsMarkup(
            searchTerm
          );

        if (
          requestId !== this.requestId
        ) {
          return;
        }

        /*
         * Shopify hittade resultat.
         *
         * Då gör vi INGENTING fuzzy.
         */
        if (
          this.hasUsableResults(
            originalMarkup
          )
        ) {
          this.correctedQuery = null;

          this.renderResults(
            originalMarkup
          );

          return;
        }

        /*
         * STEG 2:
         *
         * Shopify hittade ingenting.
         *
         * Försök hitta en rimlig
         * stavningskorrigering.
         */
        const correction =
          this.getCorrectionCandidate(
            searchTerm
          );

        if (
          correction &&
          this.normalize(correction) !==
            this.normalize(searchTerm)
        ) {
          const correctedMarkup =
            await this.fetchResultsMarkup(
              correction
            );

          if (
            requestId !== this.requestId
          ) {
            return;
          }

          /*
           * Den korrigerade termen
           * gav resultat.
           */
          if (
            this.hasUsableResults(
              correctedMarkup
            )
          ) {
            this.correctedQuery =
              correction;

            this.renderResults(
              correctedMarkup,
              correction
            );

            return;
          }
        }

        /*
         * Ingen korrigering fungerade.
         *
         * Visa Shopifys ursprungliga
         * "inga resultat".
         */
        this.correctedQuery = null;

        this.renderResults(
          originalMarkup
        );
      } catch (error) {
        if (
          requestId !== this.requestId
        ) {
          return;
        }

        console.error(error);
        this.close();
      }
    }

    escapeHtml(value) {
      const div =
        document.createElement('div');

      div.textContent = value;

      return div.innerHTML;
    }

    renderResults(
      markup,
      correctedQuery = null
    ) {
      let finalMarkup = markup;

      /*
       * Visa:
       *
       * Visar resultat för "kakel"
       *
       * om kunden skrev "kakle".
       */
      if (correctedQuery) {
        const template =
          document.createElement(
            'template'
          );

        template.innerHTML = markup;

        const searchBox =
          template.content.querySelector(
            '.predictive-search'
          );

        if (searchBox) {
          searchBox.insertAdjacentHTML(
            'afterbegin',
            `
              <div
                class="predictive-search__correction"
                role="status"
              >
                ${this.escapeHtml(
                  this.correctionLabel
                )}
                <strong>
                  “${this.escapeHtml(
                    correctedQuery
                  )}”
                </strong>
              </div>
            `
          );

          finalMarkup =
            template.innerHTML;
        }
      }

      this.results.innerHTML =
        finalMarkup;

      this.setAttribute(
        'results',
        'true'
      );

      this.open();
    }

    setLiveRegionLoadingState() {
      this.setLiveRegionText(
        this.loadingText ||
          'Laddar'
      );
    }

    setLiveRegionText(text) {
      if (!this.statusEl) {
        return;
      }

      this.statusEl.setAttribute(
        'aria-hidden',
        'false'
      );

      this.statusEl.textContent =
        text;

      setTimeout(() => {
        this.statusEl.setAttribute(
          'aria-hidden',
          'true'
        );
      }, 1000);
    }

    open() {
      if (this.overlay) {
        this.overlay.classList.add(
          'is-visible'
        );
      }

      if (
        this.resetBtn &&
        this.getQuery().length
      ) {
        this.resetBtn.hidden = false;
      }

      this.input.setAttribute(
        'aria-expanded',
        'true'
      );

      this.setAttribute(
        'open',
        ''
      );

      document.body.classList.add(
        'overlay-predictive-search'
      );

      this.keydownHandler =
        this.keydownHandler ||
        this.handleKeydown.bind(this);

      this.keyupHandler =
        this.keyupHandler ||
        this.handleKeyup.bind(this);

      this.resetHandler =
        this.resetHandler ||
        this.handleReset.bind(this);

      this.overlayHandler =
        this.overlayHandler ||
        this.close.bind(this);

      this.addEventListener(
        'keydown',
        this.keydownHandler
      );

      this.addEventListener(
        'keyup',
        this.keyupHandler
      );

      if (this.resetBtn) {
        this.resetBtn.addEventListener(
          'click',
          this.resetHandler
        );
      }

      if (this.overlay) {
        this.overlay.addEventListener(
          'click',
          this.overlayHandler
        );
      }
    }

    handleReset() {
      this.input.value = '';

      this.input.setAttribute(
        'value',
        ''
      );

      this.correctedQuery = null;

      this.removeAttribute(
        'results'
      );

      this.close();
      this.input.focus();
    }

    close() {
      const selected =
        this.querySelector(
          '.predictive-search__item[aria-selected="true"]'
        );

      if (selected) {
        selected.setAttribute(
          'aria-selected',
          'false'
        );
      }

      this.removeAttribute('open');

      if (this.resetBtn) {
        this.resetBtn.hidden = true;
      }

      if (this.overlay) {
        this.overlay.classList.remove(
          'is-visible'
        );
      }

      this.input.setAttribute(
        'aria-activedescendant',
        ''
      );

      this.input.setAttribute(
        'aria-expanded',
        'false'
      );

      document.body.classList.remove(
        'overlay-predictive-search'
      );

      if (this.keydownHandler) {
        this.removeEventListener(
          'keydown',
          this.keydownHandler
        );
      }

      if (this.keyupHandler) {
        this.removeEventListener(
          'keyup',
          this.keyupHandler
        );
      }

      if (
        this.resetBtn &&
        this.resetHandler
      ) {
        this.resetBtn.removeEventListener(
          'click',
          this.resetHandler
        );
      }

      if (
        this.overlay &&
        this.overlayHandler
      ) {
        this.overlay.removeEventListener(
          'click',
          this.overlayHandler
        );
      }
    }
  }

  customElements.define(
    'predictive-search',
    PredictiveSearch
  );
}